import { useEffect, useLayoutEffect, useRef, useState } from "react";

interface FadeWordProps {
  words: string[];
  /** How long a word stays fully visible before crossfading, in ms. */
  holdMs?: number;
  /**
   * Hold for the first word only. Shorter than holdMs so a visitor sees the
   * swap happen rather than landing on a headline that looks static.
   */
  firstHoldMs?: number;
}

/**
 * Crossfades between words in place — a quiet counterpart to RotatingWord's
 * scramble. Words are stacked absolutely and the box animates to the active
 * word's measured width, so what follows tracks the word instead of sitting
 * against a gap reserved for the longest one. Static under reduced motion.
 */
export function FadeWord({ words, holdMs = 4600, firstHoldMs = 1000 }: FadeWordProps) {
  const [index, setIndex] = useState(0);
  const [animate, setAnimate] = useState(true);
  const [width, setWidth] = useState<number | undefined>(undefined);
  const refs = useRef<(HTMLSpanElement | null)[]>([]);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setAnimate(false);
      return;
    }
    // First swap lands early, then it settles into the steady cadence.
    let interval: number | undefined;
    const kickoff = window.setTimeout(() => {
      setIndex((i) => (i + 1) % words.length);
      interval = window.setInterval(() => {
        setIndex((i) => (i + 1) % words.length);
      }, holdMs);
    }, firstHoldMs);

    return () => {
      window.clearTimeout(kickoff);
      if (interval) window.clearInterval(interval);
    };
  }, [words, holdMs, firstHoldMs]);

  // Measured, not computed from character counts — the display face is
  // proportional, so "Agentic" and "Software" differ by more than two glyphs.
  useLayoutEffect(() => {
    const el = refs.current[index];
    if (el) setWidth(el.getBoundingClientRect().width);
  }, [index, animate]);

  useEffect(() => {
    const onResize = () => {
      const el = refs.current[index];
      if (el) setWidth(el.getBoundingClientRect().width);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [index]);

  if (!animate) return <>{words[0]}</>;

  return (
    <span
      style={{
        display: "inline-block",
        position: "relative",
        width: width ? `${width}px` : undefined,
        transition: "width 900ms var(--ease-out)",
        verticalAlign: "top",
        whiteSpace: "nowrap",
      }}
    >
      {words.map((word, i) => (
        <span
          key={word}
          ref={(el) => {
            refs.current[i] = el;
          }}
          aria-hidden={i !== index}
          style={{
            position: i === index ? "relative" : "absolute",
            left: 0,
            top: 0,
            opacity: i === index ? 1 : 0,
            filter: i === index ? "blur(0)" : "blur(4px)",
            transition: "opacity 900ms var(--ease-out), filter 900ms var(--ease-out)",
            whiteSpace: "nowrap",
          }}
        >
          {word}
        </span>
      ))}
    </span>
  );
}
